import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isExpired } from 'react-jwt';
import {
  Card, Grid, Alert, Snackbar, TextField,
  Chip, Box, IconButton, Tooltip
} from '@mui/material';
import MDBox from '../../../shared/MDBox';
import MDTypography from '../../../shared/MDTypography';
import DataTable from '../../../shared/Tables/DataTable';
import { ReAuth } from '../../../authentication/reAuth';
import { verifyRolesAcess } from '../../../../services/Utils/verifyRolesAcess';
import * as ContratacaoV2Service from '../../ContratacaoV2Service';
import { IContratacaoV2Get } from '../../ContratacaoV2Type';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';

function ContratacaoV2Form() {
  const navigate = useNavigate();
  const [contratacoes, setContratacoes] = useState<IContratacaoV2Get[]>([]);
  const [filtradas, setFiltradas] = useState<IContratacaoV2Get[]>([]);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [invalidPermission, setInvalidPermission] = useState(false);
  const [alertSuccess, setAlertSuccess] = useState(false);
  const [alertError, setAlertError] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState('');
  const [msgError, setMsgError] = useState('');

  const loadData = async () => {
    try {
      const data = await ContratacaoV2Service.getAllV2();
      setContratacoes(data);
      setFiltradas(data);
    } catch (e) { console.error('Erro ao carregar contratações:', e); }
  };

  useEffect(() => {
    async function init() {
      const perm = await verifyRolesAcess('funcionarios');
      setInvalidPermission(!perm);
      setTokenExpired(isExpired(localStorage.getItem('token')));
      loadData();
    }
    init();
  }, []);

  const handleReAuthInfo = () => {
    setTokenExpired(isExpired(localStorage.getItem('token')));
    window.location.reload();
  };

  const handleFilter = (value: string) => {
    if (!value) {
      setFiltradas(contratacoes);
      return;
    }
    const lower = value.toLowerCase();
    setFiltradas(
      contratacoes.filter(c =>
        c.nomeCliente?.toLowerCase().includes(lower) ||
        c.nomePlano?.toLowerCase().includes(lower) ||
        c.tipoContrato?.toLowerCase().includes(lower)
      )
    );
  };

  const handleDownloadContrato = async (contratacao: IContratacaoV2Get) => {
    try {
      const blob = await ContratacaoV2Service.obterContratoPdf(contratacao.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Contrato_${contratacao.nomeCliente}_${contratacao.tipoContrato}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      setAlertError(true);
      setMsgError('Erro ao baixar o contrato.');
    }
  };

  const handleViewContrato = async (contratacao: IContratacaoV2Get) => {
    try {
      const blob = await ContratacaoV2Service.obterContratoPdf(contratacao.id);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      setAlertError(true);
      setMsgError('Erro ao visualizar o contrato.');
    }
  };

  const formatCurrency = (value: number) => {
    return `R$ ${Number(value).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
  };

  const columns = [
    { Header: 'Cliente', accessor: 'nomeCliente', width: '20%' },
    { Header: 'Plano', accessor: 'nomePlano', width: '15%' },
    { Header: 'Tipo', accessor: 'tipoContrato', width: '12%' },
    { Header: 'Valor', accessor: 'valorContratacaoFormatado', width: '10%' },
    { Header: 'Desconto', accessor: 'descontoFormatado', width: '10%' },
    { Header: 'Total', accessor: 'valorTotalFormatado', width: '10%' },
    { Header: 'Duração', accessor: 'duracaoFormatada', width: '8%' },
    { Header: 'Data', accessor: 'dataFormatada', width: '10%' },
    { Header: 'Ações', accessor: 'acoes', width: '5%' },
  ];

  const rows = filtradas.map(c => ({
    nomeCliente: c.nomeCliente || '-',
    nomePlano: c.nomePlano || '-',
    tipoContrato: (
      <Chip
        label={c.tipoContrato}
        size="small"
        style={{ backgroundColor: c.tipoContrato === 'Psicomotricidade' ? '#7b0b99' : '#1976d2', color: 'white' }}
      />
    ),
    valorContratacaoFormatado: formatCurrency(c.valorContratacao),
    descontoFormatado: formatCurrency(c.desconto),
    valorTotalFormatado: formatCurrency(c.valorTotal),
    duracaoFormatada: `${c.duracaoContrato} mês(es)`,
    dataFormatada: new Date(c.dataCriacao).toLocaleDateString('pt-BR'),
    acoes: (
      <Box display="flex" gap={0.5}>
        <Tooltip title="Ver contrato">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewContrato(c); }}
            style={{ color: '#7b0b99' }}>
            <PictureAsPdfIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Baixar contrato">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDownloadContrato(c); }}
            style={{ color: '#4caf50' }}>
            <PictureAsPdfIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  }));

  const handleCloseSnak = () => {
    setTimeout(() => {
      setAlertError(false);
      setAlertSuccess(false);
      setMsgError('');
      setMsgSuccess('');
    }, 1500);
  };

  const handlePageChange = () => { /* required by DataTable */ };
  const handleRowClick = () => { /* required by DataTable */ };

  return (
    <>
      {tokenExpired ? (
        <ReAuth handleAuth={handleReAuthInfo} />
      ) : (
        <>
          {invalidPermission ? (
            <Alert severity="error">Você não tem permissão para acessar esse recurso.</Alert>
          ) : (
            <MDBox pt={6} pb={3}>
              <Grid container spacing={6}>
                <Grid item xs={12}>
                  <Card>
                    <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                      <MDTypography variant="h6" color="white">
                        Contratações
                      </MDTypography>
                    </MDBox>

                    <MDBox px={2} py={2}>
                      <TextField
                        placeholder="Filtrar por cliente, plano ou tipo..."
                        size="small"
                        fullWidth
                        onChange={(e) => handleFilter(e.target.value)}
                      />
                    </MDBox>

                    <MDBox px={2}>
                      <DataTable
                        table={{ columns, rows }}
                        isSorted={true}
                        entriesPerPage={true}
                        showTotalEntries={true}
                        noEndBorder
                        pageNumber={handlePageChange}
                        onRowClick={handleRowClick}
                      />
                    </MDBox>

                    <MDBox display="flex" justifyContent="flex-end" p={2}>
                      <IconButton onClick={() => navigate('/contratacoes/cadContratacao')}
                        style={{ backgroundColor: '#fff', borderRadius: 50, width: 50, height: 50 }}>
                        <AddCircleRoundedIcon style={{ width: 40, height: 40, color: '#7b0b99' }} />
                      </IconButton>
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            </MDBox>
          )}
        </>
      )}

      {alertSuccess && (
        <Snackbar open autoHideDuration={1500} onClose={handleCloseSnak}>
          <Alert onClose={handleCloseSnak} severity="success">{msgSuccess}</Alert>
        </Snackbar>
      )}
      {alertError && (
        <Snackbar open autoHideDuration={1500} onClose={handleCloseSnak}>
          <Alert onClose={handleCloseSnak} severity="error">{msgError}</Alert>
        </Snackbar>
      )}
    </>
  );
}

export default ContratacaoV2Form;
